import React, { forwardRef, MouseEventHandler, useCallback } from 'react';
import { Placement, useListItem, useMergeRefs } from '@floating-ui/react';
import { ITooltipOption } from '../../KubitTooltip/globalTooltips';
import { IconName } from '../../Icon';
import { useSingleSelectContext } from './SingleSelectContext';
import StyledSingleSelectOption from './StyledSingleSelectOption';
import KubitTooltip from '../../KubitTooltip';
import styled from 'styled-components';
import { overflowingStyles } from '../../../styles/shareableStyles';

interface IProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  tooltipOption?: ITooltipOption;
  tooltipPlacement?: Placement;
  iconName?: IconName;
  isActive?: boolean;
}

const TruncatedLabel = styled.div`
  max-width: 100%;
  // some letters would be cut-off because of overflowing styles. Thus
  // add line-height to fix it
  line-height: 20px;
  ${overflowingStyles};
`;

const SingleSelectOption = forwardRef<HTMLButtonElement, IProps>(
  (
    {
      iconName,
      label,
      tooltipOption,
      tooltipPlacement,
      onClick,
      isActive,
      ...props
    },
    componentRef,
  ) => {
    const { getItemProps, handleSelect, activeIndex } =
      useSingleSelectContext();
    const { ref: listItemRef, index } = useListItem();
    const ref = useMergeRefs([listItemRef, componentRef]);

    const handleClick = useCallback<MouseEventHandler<HTMLButtonElement>>(
      (event) => {
        handleSelect(index);
        onClick?.(event);
      },
      [handleSelect, index, onClick],
    );

    return (
      <KubitTooltip
        {...tooltipOption}
        placement={tooltipPlacement || 'left'}
        disabled={!tooltipOption || tooltipOption.disabled}
      >
        <StyledSingleSelectOption
          ref={ref}
          iconName={iconName}
          {...props}
          {...getItemProps({
            onClick: handleClick,
          })}
          tabIndex={activeIndex === index ? 0 : -1}
          isActive={isActive}
        >
          <TruncatedLabel>{label}</TruncatedLabel>
        </StyledSingleSelectOption>
      </KubitTooltip>
    );
  },
);

export default SingleSelectOption;
